import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../auth/AuthContext";
import { BrandFooter } from "../components/brand/Footer";
import { LogoLockup } from "../components/brand/LogoLockup";
import { Button } from "../components/brand/Button";
import { CinematicCanvas, MonogramWatermark, CornerBrackets, Reveal, DrawRule, HudDot } from "../components/brand/Cinematic";

export default function Login() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const location = useLocation();

  useEffect(() => { document.title = "Sign In · Omertàbella"; }, []);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const fromSignup = new URLSearchParams(location.search).get("from") === "signup";

  const onSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <>
    <main data-testid="login-page" className="relative flex min-h-screen w-full max-w-full items-center justify-center overflow-hidden bg-brand-bg px-4 py-12 sm:px-6 sm:py-16">
      <CinematicCanvas />
      <MonogramWatermark className="right-[-22%] top-[10%]" />

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
        className="relative z-10 mx-auto flex w-full max-w-md flex-col items-center text-center"
      >
        <LogoLockup variant="login" glow />

        <Reveal delay={0.2}>
          <div className="glass-strong relative mt-14 w-full max-w-full overflow-hidden rounded-3xl p-6 sm:p-10 lg:p-12">
            <CornerBrackets size={22} />
            <div className="flex flex-col items-center text-center">
              <div className="font-mono text-[10px] uppercase tracking-[0.42em] text-brand-gold inline-flex items-center gap-2">
                <HudDot /> Creator Portal
              </div>
              <h1 data-testid="login-title"
                  className="mt-6 font-display font-normal text-brand-text text-balance"
                  style={{ fontSize: "clamp(40px, 5.5vw, 72px)", letterSpacing: "-0.035em", lineHeight: 1 }}>
                Welcome <em className="italic text-brand-gold">back.</em>
              </h1>
              <DrawRule className="mt-6" width="w-12" />
              {fromSignup && (
                <p data-testid="signup-redirect-notice" className="mt-5 font-mono text-[10px] uppercase tracking-[0.32em] text-brand-gold/70">
                  Sign-in handles new accounts — continue with Google.
                </p>
              )}
              <p className="mt-8 max-w-sm text-sm leading-relaxed text-brand-text/70 sm:text-base">
                Sign in with the email you applied with. Your role determines where you land — both routes use the same secure Google sign-in.
              </p>
              <div className="mt-10 flex w-full flex-col gap-3">
                <Button data-testid="login-creator-button" size="lg" onClick={onSignIn} className="w-full">
                  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true" fill="currentColor">
                    <path d="M21.6 12.227c0-.709-.064-1.39-.182-2.045H12v3.868h5.382a4.6 4.6 0 0 1-1.995 3.018v2.51h3.227c1.886-1.737 2.986-4.296 2.986-7.351Z" />
                    <path d="M12 22c2.7 0 4.964-.895 6.614-2.422l-3.227-2.51c-.896.6-2.04.954-3.387.954-2.605 0-4.81-1.76-5.6-4.123H3.064v2.59A9.997 9.997 0 0 0 12 22Z" opacity="0.82" />
                    <path d="M6.4 13.9a6 6 0 0 1 0-3.8V7.51H3.064a10 10 0 0 0 0 8.98L6.4 13.9Z" opacity="0.64" />
                    <path d="M12 5.977c1.469 0 2.787.505 3.825 1.498l2.864-2.864C16.96 3.045 14.696 2 12 2A9.997 9.997 0 0 0 3.064 7.51L6.4 10.1c.79-2.363 2.995-4.123 5.6-4.123Z" opacity="0.45" />
                  </svg>
                  I&apos;m a Creator · Continue with Google
                </Button>
                {/* Cosmetic split — same OAuth flow behind the second button.
                    See server.py::auth_session; a hardcoded warning in Login.jsx
                    forbids adding alternate redirect URLs. */}
                <button
                  type="button"
                  data-testid="login-admin-button"
                  onClick={onSignIn}
                  className="w-full rounded-full border border-brand-text/25 bg-transparent px-6 py-3 text-[11px] uppercase tracking-[0.28em] text-brand-text/70 transition hover:border-brand-gold/60 hover:text-brand-gold"
                >
                  I&apos;m an Admin · Sign in
                </button>
              </div>
              {/* Preserved id for any legacy testers referring to the old button. */}
              <span data-testid="google-signin-button" className="sr-only" aria-hidden="true">google-signin</span>
              <Link to="/" data-testid="login-back-home"
                    className="mt-10 font-mono text-[10px] uppercase tracking-[0.32em] text-brand-text/40 transition-colors hover:text-brand-gold">
                ← Back to home
              </Link>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.4}>
          <div className="mt-10 flex justify-center">
            <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-text/55">
              Private Influence · Public Power
            </span>
          </div>
        </Reveal>
      </motion.div>
    </main>
    <BrandFooter />
    </>
  );
}
